/**
 * MoodMapHeader - Header component with navigation and streak display
 */

import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useMoodMapStore } from '../../stores/moodmapStore';
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  BarChart3,
  Flame,
  Home,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const MoodMapHeader = () => {
  const {
    currentMonth,
    currentYear,
    viewMode,
    streak,
    goToPreviousMonth,
    goToNextMonth,
    goToToday,
    setViewMode,
    setCurrentYear,
  } = useMoodMapStore();

  const monthLabel = format(currentMonth, 'MMMM yyyy', { locale: tr });

  return (
    <header className="bg-gray-900/80 backdrop-blur-sm border-b border-gray-800 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left: Back to home + Title */}
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
              title="Ana Sayfa"
            >
              <Home className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-white">MoodMap</h1>
              <p className="text-xs text-gray-400">Günlük Yansıma</p>
            </div>
          </div>

          {/* Center: Navigation */}
          <div className="flex items-center gap-2">
            {viewMode === 'monthly' ? (
              <>
                <button
                  onClick={goToPreviousMonth}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={goToToday}
                  className="px-4 py-2 text-white font-medium hover:bg-gray-800 rounded-lg transition-colors min-w-[160px]"
                >
                  {monthLabel}
                </button>
                <button
                  onClick={goToNextMonth}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setCurrentYear(currentYear - 1)}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="px-4 py-2 text-white font-medium min-w-[100px] text-center">
                  {currentYear}
                </span>
                <button
                  onClick={() => setCurrentYear(currentYear + 1)}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
          </div>

          {/* Right: Streak + View Toggle */}
          <div className="flex items-center gap-4">
            {/* Streak */}
            {streak > 0 && (
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="flex items-center gap-2 px-3 py-1.5 bg-orange-500/20 border border-orange-500/30 rounded-full"
              >
                <Flame className="w-4 h-4 text-orange-400" />
                <span className="text-sm font-medium text-orange-400">
                  {streak} gün
                </span>
              </motion.div>
            )}

            {/* View Toggle */}
            <div className="flex items-center bg-gray-800 rounded-lg p-1">
              <button
                onClick={() => setViewMode('monthly')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'monthly'
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
                title="Aylık Görünüm"
              >
                <Calendar className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('yearly')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'yearly'
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
                title="Yıllık Görünüm"
              >
                <BarChart3 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default MoodMapHeader;
